import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Video, ArrowRight, ArrowUpRight } from 'lucide-react';
import { publicAPI } from '@/lib/publicApi';
import { fadeUp, staggerParent, viewportOnce } from './motionPresets';

const EventCard = ({ event }) => {
  const start = event.start_datetime ? new Date(event.start_datetime) : null;
  const month = start ? start.toLocaleDateString(undefined, { month: 'short' }) : '—';
  const day = start ? start.toLocaleDateString(undefined, { day: 'numeric' }) : '';
  const timeStr = start && !event.all_day ? start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : '';
  const loc = event.physical_location;
  const isVirtual = !!event.virtual_meeting_url && !loc?.city;
  const locLabel = isVirtual ? 'Virtual Event' : (loc && (loc.city || loc.state) ? [loc.city, loc.state].filter(Boolean).join(', ') : 'Location TBD');
  return (
    <motion.div variants={fadeUp}>
      <Link
        to={`/events/${event.event_id}`}
        data-testid={`event-card-${event.event_id}`}
        className="group flex gap-4 rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-xl hover:border-[#0f2a5e]/40 hover:-translate-y-1 transition-all duration-300"
      >
        <div className="shrink-0 w-14 rounded-xl bg-[#0f2a5e] text-white flex flex-col items-center justify-center py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[#f5c876]">{month}</span>
          <span className="text-xl font-bold leading-none mt-0.5">{day}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-slate-500">
            {isVirtual ? <Video size={12} /> : <MapPin size={12} />} {locLabel}{timeStr && ` · ${timeStr}`}
          </div>
          <h3 className="mt-1 text-base font-bold text-slate-900 leading-snug line-clamp-2 group-hover:text-[#0f2a5e]">{event.title}</h3>
          <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#b8862d]">Register now <ArrowUpRight size={13} /></span>
        </div>
      </Link>
    </motion.div>
  );
};

export default function EventsSection() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicAPI.listPublicEvents({ window: 'upcoming', page: 1, page_size: 3 })
      .then((r) => setEvents(r.data.items || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="events" className="scroll-mt-28 lg:scroll-mt-32 bg-white py-20 sm:py-28 lg:py-32" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div className="max-w-xl">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#b8862d]">Upcoming events</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">Connect. Learn. Grow together.</h2>
            <p className="mt-4 text-lg text-slate-600 leading-relaxed">
              Join us for in-person and virtual events designed to educate, inspire, and bring our agent community together.
            </p>
          </div>
          <Link to="/events" data-testid="events-view-all" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f2a5e] hover:gap-3 transition-all">
            View all events <ArrowRight size={15} />
          </Link>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="events-loading">
            {[0, 1, 2].map((i) => <div key={i} className="h-28 rounded-2xl bg-slate-100 animate-pulse" />)}
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-14 text-center" data-testid="events-empty">
            <span className="mx-auto w-14 h-14 rounded-2xl bg-[#0f2a5e]/5 text-[#0f2a5e] flex items-center justify-center mb-4"><Calendar size={26} /></span>
            <h3 className="text-lg font-bold text-slate-900">New events are on the way</h3>
            <p className="mt-2 text-sm text-slate-600 max-w-md mx-auto">We're planning our next round of trainings and summits. Check back soon or reach out to your KR contact.</p>
            <Link to="/become-an-agent" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0f2a5e] text-white text-sm font-semibold hover:bg-[#0a1f47] transition-colors">
              Get notified <ArrowRight size={15} />
            </Link>
          </div>
        ) : (
          <motion.div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} data-testid="events-preview-grid">
            {events.map((ev) => <EventCard key={ev.event_id} event={ev} />)}
          </motion.div>
        )}
      </div>
    </section>
  );
}
